import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMountedState } from 'react-use'

import { useControllableState } from '../../hooks'
import type { ActionHandler } from './types'

export function useActionDialogState({
  open: openProp,
  defaultOpen,
  onOpenChange,
}: {
  open: boolean | undefined
  defaultOpen: boolean | undefined
  onOpenChange: ((value: boolean) => void) | undefined
}) {
  const isMounted = useMountedState()
  const [loadingId, setLoadingId] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  })

  const dispatchAction = useCallback(
    async ({ handler, id }: { handler?: ActionHandler; id: string }) => {
      // Reset any error state beforehand
      setError(undefined)

      let handlerResult = handler?.()
      const previousFocusedElement = document.activeElement as HTMLElement

      // If handler returns a promise, we disable all buttons and set
      // a loading state on the triggered action button
      if (typeof handlerResult === 'object' && 'then' in handlerResult) {
        setLoadingId(id)

        try {
          handlerResult = await handlerResult
        } catch (e) {
          handlerResult = { error: e as string }
        }

        if (!isMounted()) {
          return
        }

        setLoadingId('')
      }

      // Just close the dialog if handler returned nothing
      if (handlerResult == null) {
        setOpen(false)

        return
      }

      // return the focus to the button
      // this is needed because we disable the buttons and the focus is lost to the body
      // however, we don't want to re-focus if something else was focused
      if (document.activeElement === document.body) {
        previousFocusedElement?.focus?.()
      }

      // Otherwise, check for an error
      const hasError = handlerResult.error != null

      if (hasError) {
        setError(handlerResult.error)
      }

      // Don't close the dialog if the action returned an error
      // or if { close: false } was explicitly set
      if (handlerResult.close !== false && !hasError) {
        setOpen(false)
      }
    },
    [isMounted, setOpen],
  )

  useEffect(
    function resetStateOnOpen() {
      if (open) {
        setError(undefined)
        setLoadingId('')
      }
    },
    [open],
  )

  return useMemo(() => {
    return {
      loadingId,
      hasActionRunning: loadingId !== '',
      error,
      open,
      setOpen,
      dispatchAction,
    }
  }, [loadingId, error, open, setOpen, dispatchAction])
}
